import { useState } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import "@/App.css";
import ValuePage from "./ValuePage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MobileMenu = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="mobile-menu-overlay" onClick={onClose}>
      <div className="mobile-menu" onClick={e => e.stopPropagation()}>
        <a href="#features" onClick={onClose}>Features</a>
        <a href="#how-it-works" onClick={onClose}>How It Works</a>
        <Link to="/value" onClick={onClose} className="nav-value-link">Business Value</Link>
        <a href="https://chromewebstore.google.com/detail/leadeyepro/lalbegpebklchglhpobgjnbjpieifcfe" target="_blank" rel="noopener noreferrer" className="mobile-menu-cta" onClick={onClose}>Add to Chrome</a>
      </div>
    </div>
  );
};

const LeadeyeProLanding = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    website: "",
    message: ""
  });
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", company: "", website: "", message: "" });
      } else {
        setSubmitStatus("error");
      }
    } catch (err) {
      setSubmitStatus("error");
    }
    
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="nav">
        <div className="nav-container">
          <div className="nav-logo">
            <span className="logo-text">LeadeyePro</span>
          </div>
          <div className="nav-links">
            <a href="#features">Features</a>
            <a href="#how-it-works">How It Works</a>
            <Link to="/value" className="nav-value-link">Business Value</Link>
            <a href="https://chromewebstore.google.com/detail/leadeyepro/lalbegpebklchglhpobgjnbjpieifcfe" target="_blank" rel="noopener noreferrer" className="nav-cta">Add to Chrome</a>
          </div>
          <button className="hamburger-btn" data-testid="hamburger-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
          </button>
        </div>
      </nav>
      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg-gradient"></div>
        <div className="hero-content">
          <div className="hero-badge">AI-Powered Sales Automation</div>
          <h1 className="hero-title">
            Turn Marketplace Inquiries Into
            <span className="gradient-text"> Closed Deals</span>
          </h1>
          <p className="hero-subtitle">
            LeadeyePro is an intelligent Chrome extension that automates your Facebook Marketplace sales workflow. 
            From instant AI responses to inventory sync, we help dealerships sell more with less effort.
          </p>
          <div className="hero-cta-group">
            <a href="#contact" className="btn btn-primary">Request a Demo</a>
            <a href="https://chromewebstore.google.com/detail/leadeyepro/lalbegpebklchglhpobgjnbjpieifcfe" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width: '18px', height: '18px', marginRight: '8px'}}>
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="4"></circle>
                <line x1="21.17" y1="8" x2="12" y2="8"></line>
                <line x1="3.95" y1="6.06" x2="8.54" y2="14"></line>
                <line x1="10.88" y1="21.94" x2="15.46" y2="14"></line>
              </svg>
              Add to Chrome
            </a>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">24/7</span>
              <span className="stat-label">AI Response</span>
            </div>
            <div className="stat">
              <span className="stat-number">10x</span>
              <span className="stat-label">Faster Follow-up</span>
            </div>
            <div className="stat">
              <span className="stat-number">100%</span>
              <span className="stat-label">Customized</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Powerful Features for Modern Dealerships</h2>
            <p className="section-subtitle">Everything you need to dominate Facebook Marketplace sales</p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
              </div>
              <h3>AI Messenger Agent</h3>
              <p>Instant, intelligent responses to customer inquiries 24/7. Our AI understands context, answers questions from your inventory specs, and guides leads toward appointments.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>
              <h3>One-Click Posting</h3>
              <p>Post listings from your inventory to Facebook Marketplace with a single click. Auto-fills all details, descriptions, and pricing directly from your website.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                </svg>
              </div>
              <h3>Price Sync</h3>
              <p>Automatically detect price changes between your inventory and Marketplace listings. Update prices in bulk or mark units as sold with one click.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <h3>Lead Management</h3>
              <p>Track hot leads, conversation history, and buyer intent. Know exactly who's ready to buy and prioritize your follow-ups accordingly.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="3" y1="9" x2="21" y2="9"></line>
                  <line x1="9" y1="21" x2="9" y2="9"></line>
                </svg>
              </div>
              <h3>Custom Integration</h3>
              <p>We tailor LeadeyePro to your specific inventory system and website. Seamless integration with your existing workflow, not a one-size-fits-all solution.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
              </div>
              <h3>Cloud Sync</h3>
              <p>Your data syncs across devices and team members. Never lose a match, never miss a lead. Enterprise-grade security for your business data.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">How LeadeyePro Works</h2>
            <p className="section-subtitle">Get up and running in minutes, not days</p>
          </div>
          
          <div className="steps-container">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Install Extension</h3>
              <p>Add LeadeyePro to Chrome and connect it to your account. Takes less than 2 minutes.</p>
            </div>
            <div className="step-connector"></div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>We Customize</h3>
              <p>Our team configures the extension for your specific inventory website and business needs.</p>
            </div>
            <div className="step-connector"></div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Start Selling</h3>
              <p>Post listings, let AI handle inquiries, and watch your Marketplace sales grow.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="section-container">
          <div className="cta-content">
            <h2>Ready to Transform Your Marketplace Sales?</h2>
            <p>Join dealerships already using LeadeyePro to close more deals with less effort.</p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="section-container">
          <div className="contact-wrapper">
            <div className="contact-info">
              <h2>Let's Talk</h2>
              <p>Every business is unique. Tell us about your dealership and we'll create a custom solution that fits your needs and budget.</p>
              
              <div className="contact-details">
                <div className="contact-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <span>support@leadeyepro.com</span>
                </div>
              </div>

              <div className="trust-badges">
                <div className="badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                    <polyline points="9 12 11 14 15 10"></polyline>
                  </svg>
                  <span>Secure & Private</span>
                </div>
                <div className="badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    value={formData.name}
                    onChange={handleChange}
                    required 
                    placeholder="John Smith"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    value={formData.email}
                    onChange={handleChange}
                    required 
                    placeholder="john@dealership.com"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company">Company/Dealership *</label>
                  <input 
                    type="text" 
                    id="company" 
                    name="company" 
                    value={formData.company}
                    onChange={handleChange}
                    required 
                    placeholder="ABC Motors"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="website">Inventory Website</label>
                  <input 
                    type="text" 
                    id="website" 
                    name="website" 
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="www.yourdealership.com"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Tell us about your needs</label>
                <textarea 
                  id="message" 
                  name="message" 
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="How many units do you list monthly? What challenges are you facing with Marketplace?"
                ></textarea>
              </div>

              <button type="submit" className="btn btn-primary btn-full" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Request Custom Quote"}
              </button>
              
              {submitStatus === "success" && (
                <div className="form-success">Thanks! We'll be in touch within 24 hours.</div>
              )}
              {submitStatus === "error" && (
                <div className="form-error">Something went wrong. Please email us directly at support@leadeyepro.com</div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-brand">
            <span className="logo-text">LeadeyePro</span>
            <p>AI-powered sales automation for Facebook Marketplace</p>
          </div>
          <div className="footer-links">
            <a href="#features">Features</a>
            <a href="#how-it-works">How It Works</a>
            <Link to="/value">Business Value</Link>
            <a href="#contact">Contact</a>
            <a href="/privacy-policy">Privacy Policy</a>
          </div>
          <div className="footer-copyright">
            © {new Date().getFullYear()} LeadeyePro. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LeadeyeProLanding />} />
        <Route path="/value" element={<ValuePage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
